import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

export type EmailEventType = 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'unsubscribed';

export interface EmailTrackingEvent {
  id: string;
  leadId: string;
  messageId: string;
  type: EmailEventType;
  timestamp: Date;
  metadata?: Record<string, any>;
  createdAt: Date;
}

export interface EmailMessage {
  id: string;
  leadId: string;
  messageId: string;
  templateId?: string;
  subject: string;
  body: string;
  toEmail: string;
  fromEmail?: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  sentAt?: Date;
  deliveredAt?: Date;
  openedAt?: Date;
  clickedAt?: Date;
  bouncedAt?: Date;
  createdAt: Date;
  createdBy: string;
}

const EMAIL_TRACKING_EVENTS_COLLECTION = 'pilotCrmEmailEvents';
const EMAIL_MESSAGES_COLLECTION = 'pilotCrmEmailMessages';

export class EmailTrackingService {
  private static docToEvent(id: string, data: any): EmailTrackingEvent {
    return {
      id,
      leadId: data.leadId,
      messageId: data.messageId,
      type: data.type,
      timestamp: data.timestamp?.toDate() || new Date(),
      metadata: data.metadata,
      createdAt: data.createdAt?.toDate() || new Date(),
    };
  }

  private static docToMessage(id: string, data: any): EmailMessage {
    return {
      id,
      leadId: data.leadId,
      messageId: data.messageId,
      templateId: data.templateId,
      subject: data.subject || '',
      body: data.body || '',
      toEmail: data.toEmail || '',
      fromEmail: data.fromEmail,
      status: data.status || 'pending',
      sentAt: data.sentAt?.toDate(),
      deliveredAt: data.deliveredAt?.toDate(),
      openedAt: data.openedAt?.toDate(),
      clickedAt: data.clickedAt?.toDate(),
      bouncedAt: data.bouncedAt?.toDate(),
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
    };
  }

  static async logEmailSent(
    leadId: string,
    messageId: string,
    subject: string,
    body: string,
    toEmail: string,
    templateId: string | undefined,
    userId: string
  ): Promise<string> {
    const now = Timestamp.now();

    const messageData = {
      leadId,
      messageId,
      templateId,
      subject,
      body,
      toEmail,
      status: 'sent',
      sentAt: now,
      createdAt: now,
      createdBy: userId,
    };

    const messageRef = await addDoc(collection(db, EMAIL_MESSAGES_COLLECTION), messageData);

    await this.logEmailEvent(leadId, messageId, 'sent', userId);

    return messageRef.id;
  }

  static async logEmailEvent(
    leadId: string,
    messageId: string,
    type: EmailEventType,
    userId: string,
    metadata?: Record<string, any>
  ): Promise<string> {
    const now = Timestamp.now();

    const eventData = {
      leadId,
      messageId,
      type,
      timestamp: now,
      metadata,
      createdAt: now,
      createdBy: userId,
    };

    const eventRef = await addDoc(collection(db, EMAIL_TRACKING_EVENTS_COLLECTION), eventData);

    if (type !== 'sent') {
      const messagesQuery = query(
        collection(db, EMAIL_MESSAGES_COLLECTION),
        where('messageId', '==', messageId)
      );
      const snapshot = await getDocs(messagesQuery);

      if (!snapshot.empty) {
        const messageDoc = snapshot.docs[0];
        const updateData: Record<string, any> = {};

        switch (type) {
          case 'delivered':
            updateData.status = 'delivered';
            updateData.deliveredAt = now;
            break;
          case 'opened':
            updateData.openedAt = now;
            break;
          case 'clicked':
            updateData.clickedAt = now;
            break;
          case 'bounced':
            updateData.status = 'failed';
            updateData.bouncedAt = now;
            break;
        }

        if (Object.keys(updateData).length > 0) {
          await updateDoc(doc(db, EMAIL_MESSAGES_COLLECTION, messageDoc.id), updateData);
        }
      }
    }

    return eventRef.id;
  }

  static async getEmailHistory(leadId: string): Promise<EmailMessage[]> {
    const q = query(
      collection(db, EMAIL_MESSAGES_COLLECTION),
      where('leadId', '==', leadId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToMessage(docSnap.id, docSnap.data()));
  }

  static async getEmailEvents(leadId: string): Promise<EmailTrackingEvent[]> {
    const q = query(
      collection(db, EMAIL_TRACKING_EVENTS_COLLECTION),
      where('leadId', '==', leadId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToEvent(docSnap.id, docSnap.data()));
  }

  static async getMessageEvents(messageId: string): Promise<EmailTrackingEvent[]> {
    const q = query(
      collection(db, EMAIL_TRACKING_EVENTS_COLLECTION),
      where('messageId', '==', messageId),
      orderBy('timestamp', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToEvent(docSnap.id, docSnap.data()));
  }

  static generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  static getEventLabel(type: EmailEventType): string {
    const labels: Record<EmailEventType, string> = {
      sent: 'Sent',
      delivered: 'Delivered',
      opened: 'Opened',
      clicked: 'Link Clicked',
      bounced: 'Bounced',
      unsubscribed: 'Unsubscribed',
    };
    return labels[type] || type;
  }

  static getEventColor(type: EmailEventType): string {
    const colors: Record<EmailEventType, string> = {
      sent: 'blue',
      delivered: 'green',
      opened: 'purple',
      clicked: 'teal',
      bounced: 'red',
      unsubscribed: 'orange',
    };
    return colors[type] || 'gray';
  }
}
